import { create } from "zustand";
import {
  api,
  type ActionItem,
  type CreateActionBody,
  type UpdateActionBody,
} from "@/lib/api";
import { ensureCanEditCurrentMap } from "@/lib/access";

interface ActionStore {
  items: ActionItem[];
  loading: boolean;
  error: string | null;

  fetchByMap: (mapId: string) => Promise<void>;
  fetchMine: () => Promise<void>;
  create: (mapId: string, body: CreateActionBody) => Promise<ActionItem>;
  update: (mapId: string, actionId: string, body: UpdateActionBody) => Promise<ActionItem>;
  delete: (mapId: string, actionId: string) => Promise<void>;
  clearError: () => void;
}

export const useActionStore = create<ActionStore>((set, get) => ({
  items: [],
  loading: false,
  error: null,

  fetchByMap: async (mapId) => {
    set({ loading: true, error: null });
    try {
      const items = await api.listActions(mapId);
      set({ items: items ?? [] });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load action items" });
    } finally {
      set({ loading: false });
    }
  },

  fetchMine: async () => {
    set({ loading: true, error: null });
    try {
      const items = await api.listMyActions();
      set({ items: items ?? [] });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load action items" });
    } finally {
      set({ loading: false });
    }
  },

  create: async (mapId, body) => {
    if (!ensureCanEditCurrentMap("create action item")) {
      throw new Error("You do not have permission to edit this map.");
    }
    set({ error: null });
    try {
      const item = await api.createAction(mapId, body);
      set({ items: [...get().items, item] });
      return item;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create action item" });
      throw e;
    }
  },

  update: async (mapId, actionId, body) => {
    if (!ensureCanEditCurrentMap("update action item")) {
      throw new Error("You do not have permission to edit this map.");
    }
    set({ error: null });
    try {
      const updated = await api.updateAction(mapId, actionId, body);
      set({ items: get().items.map((i) => (i.id === actionId ? updated : i)) });
      return updated;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to update action item" });
      throw e;
    }
  },

  delete: async (mapId, actionId) => {
    if (!ensureCanEditCurrentMap("delete action item")) {
      throw new Error("You do not have permission to edit this map.");
    }
    set({ error: null });
    try {
      await api.deleteAction(mapId, actionId);
      set({ items: get().items.filter((i) => i.id !== actionId) });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to delete action item" });
      throw e;
    }
  },

  clearError: () => set({ error: null }),
}));
